import { useMemo } from 'react';

import type { TreeSelectItemBase } from '@/components/Select/types';
import { MainText } from '@/components/Typography/NamedTypo';
import customerPropertyFetcher from '@/features/(charts)/chart-controller/fetchers/useCustomerPropertyFetcher';
import feedbackPropertyFetcher from '@/features/(charts)/chart-controller/fetchers/useFeedbackPropertyFetcher';
import {
  ACCOUNT_COLUMNS,
  CONTACT_COLUMNS,
  FEEDBACK_COLUMNS,
  PRODUCT_COLUMNS,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';
import { lowerCaseMatch } from '@/utils/util';

import productPropertyFetcher from '../fetchers/useProductPropertyFetcher';

type Props = {
  filterOption: FilterOptionT | null;
};

const useChartFieldFetcher = (props: Props) => {
  const tagTreeFetcher = feedbackPropertyFetcher.useFetchTagTree();
  const remarkTreeFetcher = feedbackPropertyFetcher.useFetchRemarkTree();
  const sourceTreeFetcher = feedbackPropertyFetcher.useFetchSourceTree();
  const accountFetcher = customerPropertyFetcher.useFetchAccountAlias();
  const contactFetcher = customerPropertyFetcher.useFetchContactAlias();
  const agentFetcher = customerPropertyFetcher.useFetchAgentAlias();
  const taxonomyTreeFetcher = feedbackPropertyFetcher.useFetchTaxonomyTree();
  const productTreeFetcher = productPropertyFetcher.useFetchProductTree();
  const optionTreeFetcher = productPropertyFetcher.useFetchOptionTree();
  const fetcher = useMemo(() => {
    let fetcher: (
      searchValue?: string,
    ) => Promise<TreeSelectItemBase<{ level?: number }>[]> = async () => {
      return [];
    };
    switch (props.filterOption?.columnKey) {
      case FEEDBACK_COLUMNS.TAXONOMY_TOPIC:
        fetcher = taxonomyTreeFetcher;
        break;
      case FEEDBACK_COLUMNS.TAG:
        fetcher = tagTreeFetcher;
        break;
      case FEEDBACK_COLUMNS.REMARKS:
        fetcher = remarkTreeFetcher;
        break;
      case SOURCE_COLUMNS.SOURCE_TYPE:
        fetcher = sourceTreeFetcher;
        break;
      case ACCOUNT_COLUMNS.NAME_SEARCH:
        fetcher = accountFetcher;
        break;
      case CONTACT_COLUMNS.NAME_SEARCH:
        fetcher = contactFetcher;
        break;
      case FEEDBACK_COLUMNS.AGENT:
      case CONTACT_COLUMNS.AGENT:
      case ACCOUNT_COLUMNS.AGENT:
        fetcher = agentFetcher;
        break;
      case PRODUCT_COLUMNS.PRODUCT:
        fetcher = productTreeFetcher;
        break;
      case PRODUCT_COLUMNS.PRODUCT_OPTION:
        fetcher = optionTreeFetcher;
        break;
      default:
        fetcher = async (searchValue?: string) => {
          return (
            props.filterOption?.filterList
              ?.filter((item) => lowerCaseMatch(item.filterName, searchValue))
              .map((item) => {
                return {
                  alias: item.filterKey || '',
                  searchValue: item.filterName || '',
                  childNodes: [],
                  node: <MainText>{item.filterOptionNode}</MainText>,
                };
              }) || []
          );
        };
    }
    return fetcher;
  }, [
    props.filterOption?.columnKey,
    props.filterOption?.filterList,
    taxonomyTreeFetcher,
    tagTreeFetcher,
    remarkTreeFetcher,
    sourceTreeFetcher,
    accountFetcher,
    contactFetcher,
    agentFetcher,
    productTreeFetcher,
    optionTreeFetcher,
  ]);

  return fetcher;
};

export default useChartFieldFetcher;
